import jsPDF from "jspdf";
import _ from "lodash";
import moment from "moment";

import { CategoryData, CategoryType, Exercise, MultiSelectResponse } from "src/revamp/types/PatientFormTypes";
import { User } from "src/services/dto/Security";
import { FooterUrl, headerUrl } from "./constants";


const PDF_MARGIN = 8;
const LINE_HEIGHT = 6;
const LINE_SPLIT_HEIGHT = 1;

const FONTS = {
  BOLD: 'PoppinsBold',
  NORMAL: 'PoppinsNormal',
}

const FONT_SIZES = {
  TITLE: 16,
  SECTION: 12,
  BODY: 11,
}

export interface PDFGeneratorState {
  doc: jsPDF;
  y: number;
  patient: User;
  formData: Record<CategoryType, CategoryData>;
}

// Helper functions
const getValue = (state: PDFGeneratorState, path: string) => {
  return _.get(state.formData, path);
};

const getMedicationName = (medication: any): string => {
  return medication.genericName || medication.name || '';
};

const getMedicationDosage = (medication: any): string => {
  return medication.strength || '';
};

const formatDate = (date: string): string => {
  return moment(date).format('MM/DD/YYYY');
};

// Drawing functions
const drawTitle = (state: PDFGeneratorState, heading: string) => {
	state.y += PDF_MARGIN;
  state.doc.setFont(FONTS.BOLD);
  state.doc.setFontSize(FONT_SIZES.TITLE);
  state.doc.text(heading, PDF_MARGIN, state.y);
  state.doc.text(moment().format('MMMM D, YYYY'), 150, state.y);
  state.y += 8;
};

const drawHeader = (state: PDFGeneratorState) => {
  state.doc.addImage(headerUrl, 'PNG', 0, 0, 216, 30);
  state.y += 25;
};

const drawFooter = (state: PDFGeneratorState) => {
  state.doc.addImage(FooterUrl, 'PNG', 63, 105, 190, 180);
  state.y += 3;
};

const drawRow = (state: PDFGeneratorState, name: string, value: string = '', suffix: string = '') => {
  const getValueString = () => `${value.toString()} ${suffix}`;
  state.doc.setFontSize(FONT_SIZES.BODY);
  state.doc.setFont(FONTS.BOLD);
  state.doc.text(name, PDF_MARGIN, state.y);
  state.doc.setFont(FONTS.NORMAL);
  const splitTitle = state.doc.splitTextToSize(getValueString(), 80);
  for (const title of splitTitle) {
    state.doc.text(title, 90, state.y);
    state.y += LINE_HEIGHT;
  }
};

const checkNewPage = (state: PDFGeneratorState) => {
  if (state.y > 250) {
    state.y = 20;
    state.doc.addPage();
    drawHeader(state);
    drawFooter(state);
  }
};

const drawValue = (state: PDFGeneratorState, value: string = '', suffix: string = '') => {
  const getValueString = () => `${value.toString()} ${suffix}`;
  state.doc.setFont(FONTS.NORMAL);

  const splitTitle = state.doc.splitTextToSize(getValueString(), 200);
  for (const title of splitTitle) {
    checkNewPage(state);
    state.doc.text(title, PDF_MARGIN, state.y);
    state.y += LINE_HEIGHT;
  }
  state.y += LINE_SPLIT_HEIGHT;
};

const drawProvider = (state: PDFGeneratorState, provider: any) => {
  drawValue(state, "Referring Provider:");
  drawValue(state, `- Name: ${provider.providerName}`);
  if (provider.credentials) {
    drawValue(state, `- Credentials: ${provider.credentials}`);
  }
  drawValue(state, `- City: ${provider.practicingCity}`);
  drawValue(state, `- Start Date: ${formatDate(provider.careStartDate)}`);
  if (provider.careEndDate) {
    drawValue(state, `- End Date: ${formatDate(provider.careEndDate)}`);
  }
};

const drawExercise = (state: PDFGeneratorState, exercise: Exercise) => {
  drawValue(state, `• ${exercise.type}`);
  if (exercise.frequency) drawValue(state, `  - Frequency (times per week): ${exercise.frequency}`);
  if (exercise.intensity) drawValue(state, `  - Intensity: ${exercise.intensity}`);
  if (exercise.device) drawValue(state, `  - Monitoring Device: ${exercise.device}`);
  if (exercise.limitations) drawValue(state, `  - Limitations: ${exercise.limitations}`);
};

const drawSectionTitle = (state: PDFGeneratorState, title: string) => {
  state.doc.setFont(FONTS.BOLD);
  state.doc.setFontSize(FONT_SIZES.SECTION);
  state.doc.text(title, PDF_MARGIN, state.y);
  state.y += LINE_HEIGHT;
};

const endSection = (state: PDFGeneratorState) => {
  state.y += 10;
  checkNewPage(state);
};

const getDesc = (item: { description?: string }, prefix: string = ": ") => {
  return item.description ? `${prefix}${item.description.trim()}` : "";
};

const listItems = (state: PDFGeneratorState, array: string[] | MultiSelectResponse[]) => {
  if (array.length === 0) {
    drawValue(state, "None");
    return;
  }

  const getText = (item: string | MultiSelectResponse) => {
    return typeof item === "string"
      ? item.trim()
      : `${item.name}${getDesc(item)}`;
  };

  array.forEach((item) => drawValue(state, `- ${getText(item)}`));
};

const drawTextBody = (state: PDFGeneratorState, text: string) => {
  drawValue(state, `"${text}"`);
  state.y += LINE_HEIGHT;
  checkNewPage(state);
};

export {
  getValue,
  getMedicationName,
  getMedicationDosage,
  formatDate,
  drawTitle,
  drawHeader,
	drawFooter,
	drawRow,
	checkNewPage,
	drawValue,
  drawProvider,
  drawExercise,
	drawSectionTitle,
	endSection,
  getDesc,
  listItems,
  drawTextBody,
};